import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Clock,
  CheckCircle,
  Heart,
  Users,
  Lightbulb,
  Star,
  GraduationCap
} from 'lucide-react';
import { jobsData } from './jobsData';
import ApplicationForm from './ApplicationForm';

const JobDetailPage = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const job = jobsData[jobId || '1'];

  if (!job) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Job Not Found</h2>
          <button
            onClick={() => navigate('/careers')}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-lg"
          >
            Back to Careers
          </button>
        </div>
      </div>
    );
  }

  const Icon = job.icon;

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white">
      {/* Back Button */}
      <div className="bg-gradient-to-br from-purple-600/5 to-blue-600/5 border-b border-purple-500/20 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/careers')}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Careers</span>
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Job Header */}
        <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-3 sm:p-4 rounded-lg">
              <Icon size={28} className="sm:w-8 sm:h-8" />
            </div>
            <div className="flex-1 w-full">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                <h1 className="text-2xl sm:text-3xl font-bold">{job.title}</h1>
                <span className="bg-purple-500/10 px-3 py-1 rounded-full text-xs sm:text-sm text-purple-400 border border-purple-500/30 w-fit">
                  {job.department}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 text-sm sm:text-base text-gray-300 mb-4">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="flex-shrink-0" />
                  <span className="truncate">{job.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} className="flex-shrink-0" />
                  <span>{job.type} • {job.experience}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About the Role */}
        <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4">About the Role</h2>
          <p className="text-gray-300 leading-relaxed">{job.description}</p>
        </div>

        {/* Key Responsibilities */}
        <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4">Key Responsibilities</h2>
          <ul className="space-y-3">
            {job.responsibilities.map((resp: string, index: number) => (
              <li key={index} className="flex items-start gap-3 text-gray-300">
                <CheckCircle size={20} className="text-purple-400 mt-0.5 flex-shrink-0" />
                <span>{resp}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Skills & Qualifications */}
        {job.skillsAndQualifications && job.skillsAndQualifications.length > 0 && (
          <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
            <h2 className="text-xl md:text-2xl font-bold mb-4">Skills & Qualifications</h2>
            <ul className="space-y-3">
              {job.skillsAndQualifications.map((item: string, index: number) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <Star size={20} className="text-purple-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Preferred Experience */}
        {job.preferredExperience && job.preferredExperience.length > 0 && (
          <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
            <h2 className="text-xl md:text-2xl font-bold mb-4">Preferred Experience</h2>
            <ul className="space-y-3">
              {job.preferredExperience.map((item: string, index: number) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <GraduationCap size={20} className="text-purple-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Management and Interpersonal Skills */}
        {job.managementSkills && job.managementSkills.length > 0 && (
          <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
            <h2 className="text-xl md:text-2xl font-bold mb-4">Management and Interpersonal Skills</h2>
            <ul className="space-y-3">
              {job.managementSkills.map((item: string, index: number) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <Users size={20} className="text-purple-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Judgement and Decision Making */}
        {job.judgement && job.judgement.length > 0 && (
          <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
            <h2 className="text-xl md:text-2xl font-bold mb-4">Judgement and Decision Making</h2>
            <ul className="space-y-3">
              {job.judgement.map((item: string, index: number) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <Lightbulb size={20} className="text-purple-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Required Skills */}
        <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4">Required Skills</h2>
          <div className="flex flex-wrap gap-3">
            {job.skills.map((skill: string, index: number) => (
              <span
                key={index}
                className="bg-purple-500/10 px-4 py-2 rounded-lg text-sm border border-purple-500/20 text-gray-300"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Benefits & Perks */}
        <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4">Benefits & Perks</h2>
          <ul className="space-y-3">
            {job.benefits.map((benefit: string, index: number) => (
              <li key={index} className="flex items-start gap-3 text-gray-300">
                <Heart size={20} className="text-purple-400 mt-0.5 flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Requirements — only render if data exists (for jobs that use it) */}
        {job.requirements && job.requirements.length > 0 && (
          <div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-4 md:p-8 mb-8">
            <h2 className="text-xl md:text-2xl font-bold mb-4">Requirements</h2>
            <ul className="space-y-3">
              {job.requirements.map((req: string, index: number) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <CheckCircle size={20} className="text-purple-400 mt-0.5 flex-shrink-0" />
                  <span>{req}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Application Form */}
        <ApplicationForm jobTitle={job.title} />
      </div>
    </div>
  );
};

export default JobDetailPage;